// React Hooks for Task Nomenclatures
// Data fetching and mutations for all 18 task nomenclature types

import { useState, useEffect } from 'react';
import { taskNomenclatureService, NOMENCLATURE_TYPES } from '../services/taskNomenclatureService';

/**
 * Generic hook to fetch nomenclature data
 */
export function useTaskNomenclature(nomenclatureType) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await taskNomenclatureService.getAll(nomenclatureType);
      setItems(data);
    } catch (err) {
      console.error(`Error loading ${nomenclatureType}:`, err);
      setError(err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [nomenclatureType]);

  const refresh = () => {
    loadItems();
  };

  return { items, loading, error, refresh };
}

/**
 * Hook for nomenclature mutations (create, update, delete)
 */
export function useTaskNomenclatureMutations(nomenclatureType) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const create = async (data, userId) => {
    try {
      setLoading(true);
      setError(null);
      const id = await taskNomenclatureService.create(nomenclatureType, data, userId);
      return id;
    } catch (err) {
      console.error(`Error creating ${nomenclatureType}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const update = async (id, updates, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskNomenclatureService.update(nomenclatureType, id, updates, userId);
    } catch (err) {
      console.error(`Error updating ${nomenclatureType}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (id, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskNomenclatureService.delete(nomenclatureType, id, userId);
    } catch (err) {
      console.error(`Error deleting ${nomenclatureType}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const reorder = async (orderedIds, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskNomenclatureService.reorder(nomenclatureType, orderedIds, userId);
    } catch (err) {
      console.error(`Error reordering ${nomenclatureType}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    create,
    update,
    delete: deleteItem,
    reorder,
    loading,
    error
  };
}

// ==================== SPECIFIC NOMENCLATURE HOOKS ====================

export function useTaskTypes() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.TASK_TYPES);
}

export function useTaskStatuses() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.TASK_STATUSES);
}

export function useTaskPriorities() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.TASK_PRIORITIES);
}

export function useTaskUrgencies() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.TASK_URGENCIES);
}

export function useAssignmentTypes() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.ASSIGNMENT_TYPES);
}

export function useRecurrenceFrequencies() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.RECURRENCE_FREQUENCIES);
}

export function usePatientConditions() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.PATIENT_CONDITIONS);
}

export function useIsolationLevels() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.ISOLATION_LEVELS);
}

export function useEquipmentTypesTask() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.EQUIPMENT_TYPES_TASK);
}

export function useProcedureTypes() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.PROCEDURE_TYPES);
}

export function useEnvironmentalRisks() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.ENVIRONMENTAL_RISKS);
}

export function useShiftTypes() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.SHIFT_TYPES);
}

export function useCompletionQualities() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.COMPLETION_QUALITIES);
}

export function useDeviationTypes() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.DEVIATION_TYPES);
}

export function useRiskLevels() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.RISK_LEVELS);
}

export function useGroupTypes() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.GROUP_TYPES);
}

export function useWorkloadBalancingMethods() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.WORKLOAD_BALANCING_METHODS);
}

export function useNotificationChannels() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.NOTIFICATION_CHANNELS);
}

export function useTriggerEvents() {
  return useTaskNomenclature(NOMENCLATURE_TYPES.TRIGGER_EVENTS);
}


